import axios from 'axios';
import semver from 'semver';
import {notification} from 'antd';
import {Subject} from 'rxjs';
import {get} from 'lodash';

export class Backend {
    constructor({url, username, role, email, customerId, accessToken}) {
        this.url = url;
        this.username = username;
        this.role = role;
        this.email = email;
        this.customerId = customerId;
        this.accessToken = accessToken;
        this.axios = axios.create({
            headers: {
                Authorization: `Bearer ${accessToken.sessionId}`,
                'X-Acuwize-CustomerId': window.customerId,
            },
            params: {
                token: accessToken.sessionId,
                // TEMPORARY (dev multi-user sim): when NODE_ENV !== production,
                // the server's enforce-role falls back to these query params
                // instead of the single .env stub, so two browser tabs can
                // simulate two different users without a real SSO wired up.
                // Prod ignores them (SSO is the only trusted source).
                user: username,
                role: role,
                email: email,
            },
        });
        // Failed responses get surfaced to the browser console in one
        // structured group so that 500s coming back from the server can
        // be diagnosed without shelling into the backend host. The
        // original error still bubbles up so per-call try/catch runs.
        this.axios.interceptors.response.use(
            (response) => response,
            (error) => {
                const cfg = error.config || {};
                const res = error.response || {};
                const label = `[backend ${res.status || 'ERR'}] ${String(cfg.method || 'GET').toUpperCase()} ${cfg.url || '(no url)'}`;
                console.groupCollapsed(label);
                console.log('status', res.status, res.statusText);
                if (cfg.data) console.log('request body', cfg.data);
                if (res.data !== undefined) console.log('response body', res.data);
                if (res.headers) console.log('response headers', res.headers);
                if (error.message) console.log('message', error.message);
                console.groupEnd();
                return Promise.reject(error);
            },
        );
        this.event$ = new Subject();
    }
    async loadSpec({category, key, version}) {
        try {
            let url = '';
            if (version === 'latest') {
                url = `${this.url}/specs/${category}/${key}`;
            } else {
                url = `${this.url}/specs/${category}/${key}/${version}`;
            }
            const {data} = await this.axios.get(url);
            return data;
        } catch (err) {
            console.error(err);
            notification.error({title: 'Unable to load spec. Please wait and retry', description: err.message, placement: 'bottomRight'});
            return [];
        }
    }
    async loadTags({category, key}) {
        try {
            const {data} = await this.axios.post(`${this.url}/tags`, {path: `${category}/${key}`});
            return data;
        } catch (err) {
            console.error(err);
        }
    }
    async loadVersions(category, key) {
        try {
            const {data} = await this.axios.get(`${this.url}/versions/${category}/${key}`);
            return data.sort((v1, v2) => (semver.gt(v1, v2) ? -1 : 1));
        } catch (err) {
            console.error(err);
        }
    }
    async commitChanges({category, key, spec, message}) {
        try {
            const {data} = await this.axios.post(`${this.url}/${category}/${key}`, {spec, message});
            this.event$.next({type: 'commit-changes', data});
            return data;
        } catch (err) {
            console.error(err);
            notification.error({title: 'Unable to commit changes', description: err.message, placement: 'bottomRight'});
            return null;
        }
    }
    async resolveRef(ref, context) {
        try {
            const {data} = await this.axios.post(`${this.url}/resolve-ref`, {ref, context, branch: this.customerId});
            return data;
        } catch (err) {
            console.error(err);
        }
    }
    async loadLibrary() {
        try {
            const {data} = await this.axios.get(`${this.url}/library`);
            return data;
        } catch (err) {
            console.error(err);
            return [];
        }
    }

    downloadYml(category, key, version) {
        const sessionId = get(this.accessToken, 'sessionId');
        try {
            let url = `${this.url}/files/${category}/${key}`;
            if (version) {
                url += `/${version}`;
            }
            window.open(`${url}?token=${sessionId}`, '_blank');
        } catch (err) {
            console.error(err);
        }
    }

    async lintChanges(category, key, version) {
        try {
            let url = `${this.url}/lint/${category}/${key}`;
            if (version) {
                url += `/${version}`;
            }
            const {data} = await this.axios.get(url);
            return data;
        } catch (err) {
            console.error(err);
        }
    }

    async lintModel(category, key, version, modelKey) {
        try {
            const {data} = await this.axios.get(`${this.url}/lint-model/${category}/${key}/${version}/${modelKey}`);
            return data;
        } catch (err) {
            console.error(err);
        }
    }

    async lintInterface(category, key, version, intfKey) {
        try {
            console.log('linting interface', intfKey);
            const {data} = await this.axios.get(`${this.url}/lint-interface/${category}/${key}/${version}`, {params: {intfKey}});
            return data;
        } catch (err) {
            console.error(err);
        }
    }

    async uploadSpecFile(fields) {
        const form = new FormData();
        form.append('yml', fields.specFile);
        form.append('workgroup', fields.category);
        form.append('name', fields.name);
        form.append('message', fields.message);
        form.append('empty', fields.empty);
        form.append('customerId', this.customerId);
        form.append('force', fields.force);
        const {data} = await this.axios.post(`${this.url}/specs/${fields.category}/${fields.name}`, form);
        return data;
    }
    async loadModelRefs({category, key, from, env, types = null}) {
        const {data} = await this.axios.get(`${this.url}/model-refs/${category}/${key}`, {params: {from, env, types}});
        return data;
    }
    async uploadImage(file, workgroup, key) {
        console.log('uploading image', file, workgroup, key);
        const form = new FormData();
        form.append('upload', file);
        const {data} = await this.axios.post(`${this.url}/images/${workgroup}/${key}`, form);
        return data.url;
    }
    async resetSandbox() {
        const resp = await this.axios.delete(`${this.url}/sandboxes/default`);
        this.event$.next({type: 'reset-sandbox'});
        return resp;
    }
    async rollback({workgroup, key}) {
        const resp = await this.axios.delete(`${this.url}/${workgroup}/${key}/changes`);
        this.event$.next({type: 'reset-sandbox'});
        return resp.data;
    }
    async syncChanges({workgroup, key}) {
        const {data} = await this.axios.put(`${this.url}/${workgroup}/${key}/changes`);
        this.event$.next({type: 'reset-sandbox'});
        return data;
    }
    async publishVersion(workgroup, key, details) {
        const resp = await this.axios.put(`${this.url}/${workgroup}/${key}`, details);
        this.event$.next({type: 'reset-sandbox'});
        return resp.data;
    }
}
